// "Yaklaşan tatiller" — the next public/company holidays from today, read from
// the employee-readable GET /v1/leave/holidays (emp-leave.jsx side card).

import { useTranslation } from "react-i18next";

import { Badge, Card, CardBody, SectionTitle } from "@novemai/components";

import { useTimeZone } from "@/api/use-time-zone";
import { addDays, todayKey } from "@/lib/calendar-window";
import { formatDate } from "@/lib/date-range";

import { useHolidays } from "./leave-window-api";

// One year ahead; the server caps a range at 400 days.
const HORIZON_DAYS = 365;
const SHOWN = 5;

export function UpcomingHolidays(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const today = todayKey(useTimeZone());
  const query = useHolidays({ from: today, to: addDays(today, HORIZON_DAYS) });
  const items = (query.data ?? []).slice(0, SHOWN);

  return (
    <Card>
      <CardBody>
        <SectionTitle as="h2">{t("leave.balance.upcoming_holidays_title")}</SectionTitle>
        {query.isPending ? (
          <p className="m-0 font-sans text-[12.5px] text-[var(--fg-muted)]">
            {t("leave.balance.upcoming_holidays_loading")}
          </p>
        ) : query.isError ? (
          <p role="alert" className="m-0 font-sans text-[12.5px] text-[var(--state-danger)]">
            {t("leave.balance.upcoming_holidays_error")}
          </p>
        ) : items.length === 0 ? (
          <p className="m-0 font-sans text-[12.5px] text-[var(--fg-muted)]">
            {t("leave.balance.upcoming_holidays_empty")}
          </p>
        ) : (
          <ul className="m-0 list-none divide-y divide-[var(--border-subtle)] p-0">
            {items.map((holiday) => (
              <li key={holiday.holiday_id} className="flex items-center justify-between gap-3 py-2">
                <span className="font-sans text-[13px] text-[var(--fg-primary)]">{holiday.name}</span>
                <span className="flex shrink-0 items-center gap-2">
                  {holiday.kind === "half_day" && (
                    <Badge size="sm" tone="warning">
                      {t("leave.balance.holiday_half_day")}
                    </Badge>
                  )}
                  <span className="font-mono text-[12px] text-[var(--fg-muted)]">
                    {formatDate(i18n.language, holiday.date)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
